// @flow
// force flow to type check this file - https://github.com/zapier/zapier/pull/31127

import React /* eslint-disable-line no-unused-vars */, {
  type Node,
} from 'react';

import { formatTimeSpan } from '@zapier/date';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import UsageBar from './UsageBar';

/** @jsx isolateComponent */

export type Props = {|
  /**
   * Content to render at bottom of section. Typically used to
   * render a CTA to upgrade plan or similar.
   */
  children?: Node,
  /**
   * URL to where the plan can be managed.
   */
  managePlanHref: string,
  /**
   * Number of tasks that have been used in the usage period.
   */
  numTasks: number,
  /**
   * Maximum number of tasks that can be used in the usage period.
   */
  numTasksMax: number,
  /**
   * Number of Zaps that are currently on.
   */
  numZaps: number,
  /**
   * Maximum number of Zaps that can be on.
   */
  numZapsMax: number,
  /**
   * String representation of when usage resets.
   */
  resetDate: string,
  /**
   * The title of the section within the sidebar.
   */
  title: string,
|};

const Styles = labelStyles(__filename, {
  root: {
    display: 'grid',
    gap: 10,
    whiteSpace: 'nowrap',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 20,
    alignItems: 'center',
    ...Typography.base,
    fontSize: 16,
    fontWeight: 600,
    color: Colors.grey700,
  },
  content: {
    margin: '0 5px',
  },
  usages: {
    display: 'grid',
    gap: 5,
    marginBottom: 5,
  },
  resetInfo: {
    display: 'grid',
    gap: 10,
    gridTemplateColumns: '1fr auto',
    ...Typography.base,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '20px',
    color: Colors.grey500,
  },
  managePlanLink: {
    fontWeight: 700,
    textDecoration: 'underline',
    '&:hover, &:focus': {
      color: Colors.blue500,
    },
  },
});

const Usages = (props: Props) => {
  return (
    <div css={Styles.usages}>
      <UsageBar label="Tasks" max={props.numTasksMax} value={props.numTasks} />
      <UsageBar label="Zaps" max={props.numZapsMax} value={props.numZaps} />
    </div>
  );
};

/**
 * Renders account usage data within the `UniversalSidebar`.
 */
const UniversalSidebarUsage = (props: Props) => {
  const relativeDate = formatTimeSpan(Date.now(), props.resetDate, false);
  return (
    <section css={Styles.root}>
      <header css={Styles.header}>
        <Icon name="miscWallet" size={30} />
        <h3>{props.title}</h3>
      </header>
      <div css={Styles.content}>
        <Usages {...props} />
        <p css={Styles.resetInfo}>
          <span>Monthly usage resets in {relativeDate}</span>
          <Link
            color="inherit"
            css={Styles.managePlanLink}
            href={props.managePlanHref}
          >
            Manage Plan
          </Link>
        </p>
      </div>
      {props.children}
    </section>
  );
};

UniversalSidebarUsage.defaultProps = {
  managePlanHref: '/app/settings/billing',
  title: 'Usage',
};

export default UniversalSidebarUsage;
